/**
 * 공통 양식 레지스트리 · 매칭 · 기관 바인딩 (PoC localStorage)
 * SSOT: D:\295\erp\CM\design\05_양식레지스트리_매칭.md
 */
import { DOC_TYPE } from 'system/formDocTypes';
import {
  isBdApiEnabled,
  POC_BUCKET,
  loadPocDoc,
  upsertPocDoc,
  enqueuePersist,
} from 'services/pocRowPersist';
import {
  FORM_ID_GOYANG_CULTURE_V1,
  buildGoyangCultureV1FormMeta,
  BIZ_FORM_CATALOG_KEY,
  BIZ_FORM_TENANT_BIND_PREFIX,
} from 'budget/bizFormCatalogSeed';
import {
  FORM_ID_DJ_WATERWORKS_V1,
  DJ_WATERWORKS_V1_FORM,
  DJ_WATERWORKS_V1_FIELDS,
} from 'expense/resolutionFormFieldMap';

export const FORM_REGISTRY_KEY = 'erp.cm.formRegistry';
export const FORM_MATCH_KEY = 'erp.cm.formMatch';
export const FORM_ACL_KEY = 'erp.cm.formAcl';
export const FORM_TENANT_BIND_PREFIX = 'erp.cm.formTenantBind';

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function writeJson(key, value, persist = false) {
  localStorage.setItem(key, JSON.stringify(value));
  if (persist) persistFormDoc(key, value);
}

function persistFormDoc(key, value) {
  if (!isBdApiEnabled()) return;
  if (key === FORM_REGISTRY_KEY) {
    enqueuePersist(key, () =>
      upsertPocDoc({
        bucket: POC_BUCKET.FORM_CATALOG,
        rowUid: 'catalog',
        payload: value,
        globalYn: true,
      })
    );
  } else if (key === FORM_MATCH_KEY) {
    enqueuePersist(key, () =>
      upsertPocDoc({
        bucket: POC_BUCKET.FORM_MATCH,
        rowUid: 'match',
        payload: value,
        globalYn: true,
      })
    );
  } else if (key === FORM_ACL_KEY) {
    enqueuePersist(key, () =>
      upsertPocDoc({
        bucket: POC_BUCKET.FORM_ACL,
        rowUid: 'acl',
        payload: value,
        globalYn: true,
      })
    );
  } else if (String(key).startsWith(FORM_TENANT_BIND_PREFIX)) {
    const tenantCode = String(key.slice(FORM_TENANT_BIND_PREFIX.length).replace(/^\|/, ''));
    enqueuePersist(key, () =>
      upsertPocDoc({
        bucket: POC_BUCKET.FORM_BIND,
        rowUid: tenantCode || 'bind',
        payload: value,
        params: { tenantCode },
      })
    );
  }
}

function bindKey(tenantCode) {
  return `${FORM_TENANT_BIND_PREFIX}|${String(tenantCode || '').trim()}`;
}

function buildExpenseResolutionFormMeta() {
  return {
    formId: FORM_ID_DJ_WATERWORKS_V1,
    docType: DOC_TYPE.EXP_RESOLUTION,
    formName: DJ_WATERWORKS_V1_FORM.formName,
    description: '대전광역시상수도사업본부 지출결의서 (누름틀본)',
    publicUrl: DJ_WATERWORKS_V1_FORM.publicUrl,
    sourceUrl: DJ_WATERWORKS_V1_FORM.sourceUrl,
    templateFile: 'form-dj-waterworks-v1.hwp',
    fieldMapId: FORM_ID_DJ_WATERWORKS_V1,
    mapVersion: 1,
    module: 'BD',
    useYn: 'Y',
    fields: DJ_WATERWORKS_V1_FIELDS,
    subjectLeafField: DJ_WATERWORKS_V1_FORM.subjectLeafField,
    projectLeafField: DJ_WATERWORKS_V1_FORM.projectLeafField,
    createdAt: new Date().toISOString(),
    note: '시드. 재생성: npm run seed:resolution-form-fields',
  };
}

function buildBizPlanFormMeta() {
  const base = buildGoyangCultureV1FormMeta();
  return {
    ...base,
    docType: DOC_TYPE.BIZ_PLAN,
    mapVersion: 1,
    module: 'BD',
  };
}

/** 구 bizFormCatalog / tenantBind → 레지스트리 이관 */
function migrateLegacyBizCatalog(registry) {
  const legacy = readJson(BIZ_FORM_CATALOG_KEY, null);
  if (!legacy?.forms?.length) return registry;
  const byId = new Map((registry.forms || []).map((f) => [f.formId, f]));
  legacy.forms.forEach((f) => {
    if (!f?.formId) return;
    const existing = byId.get(f.formId);
    byId.set(f.formId, {
      ...(existing || {}),
      ...f,
      docType: f.docType || DOC_TYPE.BIZ_PLAN,
      mapVersion: f.mapVersion || existing?.mapVersion || 1,
      module: f.module || 'BD',
    });
  });
  return { ...registry, forms: [...byId.values()], updatedAt: new Date().toISOString() };
}

function ensureSeedForms(registry) {
  const byId = new Map((registry.forms || []).map((f) => [f.formId, f]));
  const seeds = [buildBizPlanFormMeta(), buildExpenseResolutionFormMeta()];
  seeds.forEach((s) => {
    if (!byId.has(s.formId)) byId.set(s.formId, s);
    else {
      const cur = byId.get(s.formId);
      // 시드 필드맵이 비었으면 보강
      if (!(cur.fields || []).length && (s.fields || []).length) {
        byId.set(s.formId, { ...cur, ...s, fields: s.fields });
      }
      if (!cur.docType) byId.set(s.formId, { ...byId.get(s.formId), docType: s.docType });
    }
  });
  return { ...registry, forms: [...byId.values()] };
}

export async function hydrateFormRegistry() {
  if (!isBdApiEnabled()) return ensureFormRegistry();
  const doc = await loadPocDoc({
    bucket: POC_BUCKET.FORM_CATALOG,
    rowUid: 'catalog',
    globalYn: true,
  });
  if (doc?.forms) localStorage.setItem(FORM_REGISTRY_KEY, JSON.stringify(doc));
  const match = await loadPocDoc({
    bucket: POC_BUCKET.FORM_MATCH,
    rowUid: 'match',
    globalYn: true,
  });
  if (match) localStorage.setItem(FORM_MATCH_KEY, JSON.stringify(match));
  const acl = await loadPocDoc({
    bucket: POC_BUCKET.FORM_ACL,
    rowUid: 'acl',
    globalYn: true,
  });
  if (acl) localStorage.setItem(FORM_ACL_KEY, JSON.stringify(acl));
  return ensureFormRegistry();
}

export function ensureFormRegistry() {
  let data = readJson(FORM_REGISTRY_KEY, null);
  if (!data?.forms) {
    data = { forms: [], updatedAt: new Date().toISOString() };
  }
  data = migrateLegacyBizCatalog(data);
  data = ensureSeedForms(data);
  writeJson(FORM_REGISTRY_KEY, data);
  return data;
}

export function listForms(filter = {}) {
  const forms = ensureFormRegistry().forms || [];
  return forms.filter((f) => {
    if (filter.docType && f.docType !== filter.docType) return false;
    if (filter.useYn && f.useYn !== filter.useYn) return false;
    if (filter.formId && f.formId !== filter.formId) return false;
    return true;
  });
}

export function listFormsByDocType(docType) {
  return listForms({ docType });
}

export function getForm(formId) {
  if (!formId) return null;
  return listForms().find((f) => f.formId === formId) || null;
}

export function upsertForm(form) {
  const reg = ensureFormRegistry();
  const idx = reg.forms.findIndex((f) => f.formId === form.formId);
  const next = {
    ...form,
    mapVersion: form.mapVersion || 1,
    updatedAt: new Date().toISOString(),
  };
  if (idx >= 0) reg.forms[idx] = { ...reg.forms[idx], ...next };
  else reg.forms.push({ ...next, createdAt: new Date().toISOString() });
  reg.updatedAt = new Date().toISOString();
  writeJson(FORM_REGISTRY_KEY, reg);
  persistFormDoc(FORM_REGISTRY_KEY, reg);
  return reg.forms;
}

/** 기관 × docType 바인딩 */
export function getTenantFormBindings(tenantCode, docType) {
  const tc = String(tenantCode || '').trim();
  const key = bindKey(tc);
  let pack = readJson(key, null);
  if (!pack?.byDocType) {
    pack = migrateLegacyTenantBind(tc) || {
      tenantCode: tc,
      byDocType: {},
      updatedAt: new Date().toISOString(),
    };
  }
  if (!pack.byDocType[DOC_TYPE.BIZ_PLAN]?.bindings?.length) {
    pack.byDocType[DOC_TYPE.BIZ_PLAN] = {
      bindings: [{ formId: FORM_ID_GOYANG_CULTURE_V1, activeYn: 'Y', boundAt: new Date().toISOString() }],
    };
  }
  if (!pack.byDocType[DOC_TYPE.EXP_RESOLUTION]?.bindings?.length) {
    pack.byDocType[DOC_TYPE.EXP_RESOLUTION] = {
      bindings: [{ formId: FORM_ID_DJ_WATERWORKS_V1, activeYn: 'Y', boundAt: new Date().toISOString() }],
    };
  }
  writeJson(key, pack, true);
  if (!docType) return pack;
  if (!pack.byDocType[docType]) {
    pack.byDocType[docType] = { bindings: [] };
    writeJson(key, pack, true);
  }
  return pack.byDocType[docType];
}

function migrateLegacyTenantBind(tenantCode) {
  const legacy = readJson(`${BIZ_FORM_TENANT_BIND_PREFIX}|${tenantCode}`, null);
  if (!legacy?.bindings?.length) return null;
  return {
    tenantCode,
    byDocType: {
      [DOC_TYPE.BIZ_PLAN]: { bindings: legacy.bindings },
    },
    updatedAt: new Date().toISOString(),
    migratedFrom: BIZ_FORM_TENANT_BIND_PREFIX,
  };
}

export function setTenantActiveForm(tenantCode, docType, formId) {
  const tc = String(tenantCode || '').trim();
  const pack = getTenantFormBindings(tc);
  const slot = pack.byDocType[docType] || { bindings: [] };
  if (!slot.bindings.some((b) => b.formId === formId)) {
    slot.bindings.push({ formId, activeYn: 'N', boundAt: new Date().toISOString() });
  }
  slot.bindings = slot.bindings.map((b) => ({
    ...b,
    activeYn: b.formId === formId ? 'Y' : 'N',
  }));
  pack.byDocType[docType] = slot;
  pack.updatedAt = new Date().toISOString();
  writeJson(bindKey(tc), pack, true);
  return slot;
}

export function bindFormToTenant(tenantCode, docType, formId, { makeActive = true } = {}) {
  const tc = String(tenantCode || '').trim();
  const pack = getTenantFormBindings(tc);
  const slot = pack.byDocType[docType] || { bindings: [] };
  if (!slot.bindings.some((b) => b.formId === formId)) {
    slot.bindings.push({ formId, activeYn: 'N', boundAt: new Date().toISOString() });
  }
  pack.byDocType[docType] = slot;
  writeJson(bindKey(tc), pack, true);
  if (makeActive) return setTenantActiveForm(tc, docType, formId);
  return slot;
}

/** 활성 규칙만 (resolve용). includeDisabled 시 관리 UI용 전체 */
export function listMatchRules(filter = {}) {
  const pack = readJson(FORM_MATCH_KEY, { rules: [] });
  return (pack.rules || []).filter((r) => {
    if (!filter.includeDisabled && r.useYn === 'N') return false;
    if (filter.docType && r.docType !== filter.docType) return false;
    if (filter.tenantCode && r.tenantCode !== filter.tenantCode && r.tenantCode !== '*') return false;
    if (filter.formId && r.formId !== filter.formId) return false;
    return true;
  });
}

export function upsertMatchRule(rule) {
  const pack = readJson(FORM_MATCH_KEY, { rules: [] });
  const id = rule.ruleId || `rule-${Date.now().toString(36)}`;
  const next = {
    ruleId: id,
    tenantCode: String(rule.tenantCode || '').trim() || '*',
    docType: String(rule.docType || '').trim(),
    formId: String(rule.formId || '').trim(),
    deptCode: String(rule.deptCode || '').trim(),
    projectCode: String(rule.projectCode || '').trim(),
    fundCode: String(rule.fundCode || '').trim(),
    fiscalYear: String(rule.fiscalYear || '').trim(),
    priority: Number(rule.priority) || 0,
    useYn: rule.useYn === 'N' ? 'N' : 'Y',
    updatedAt: new Date().toISOString(),
  };
  if (!next.docType) throw new Error('docType이 필요합니다.');
  if (!next.formId) throw new Error('formId가 필요합니다.');
  const idx = pack.rules.findIndex((r) => r.ruleId === id);
  if (idx >= 0) pack.rules[idx] = { ...pack.rules[idx], ...next };
  else pack.rules.push({ ...next, createdAt: new Date().toISOString() });
  pack.updatedAt = new Date().toISOString();
  writeJson(FORM_MATCH_KEY, pack, true);
  return next;
}

/** 소프트 삭제(useYn=N). hard=true 시 목록에서 제거 */
export function deleteMatchRule(ruleId, { hard = false } = {}) {
  const id = String(ruleId || '').trim();
  if (!id) return false;
  const pack = readJson(FORM_MATCH_KEY, { rules: [] });
  const idx = pack.rules.findIndex((r) => r.ruleId === id);
  if (idx < 0) return false;
  if (hard) pack.rules.splice(idx, 1);
  else pack.rules[idx] = { ...pack.rules[idx], useYn: 'N', updatedAt: new Date().toISOString() };
  pack.updatedAt = new Date().toISOString();
  writeJson(FORM_MATCH_KEY, pack, true);
  return true;
}

/**
 * 양식 사용 ACL — 부서·담당(로그인ID)
 * allowDeptCodes / allowUserIds 모두 비어 있으면 전원 허용.
 * 하나라도 있으면: 부서 일치 OR 사용자 일치.
 */
export function listFormAcls(filter = {}) {
  const pack = readJson(FORM_ACL_KEY, { acls: [] });
  return (pack.acls || []).filter((a) => {
    if (!filter.includeDisabled && a.useYn === 'N') return false;
    if (filter.tenantCode && a.tenantCode !== filter.tenantCode) return false;
    if (filter.formId && a.formId !== filter.formId) return false;
    return true;
  });
}

export function upsertFormAcl(acl) {
  const pack = readJson(FORM_ACL_KEY, { acls: [] });
  const id = acl.aclId || `acl-${Date.now().toString(36)}`;
  const next = {
    aclId: id,
    tenantCode: String(acl.tenantCode || '').trim(),
    formId: String(acl.formId || '').trim(),
    allowDeptCodes: normalizeCodeList(acl.allowDeptCodes ?? acl.deptCodes),
    allowUserIds: normalizeCodeList(acl.allowUserIds ?? acl.userIds),
    useYn: acl.useYn === 'N' ? 'N' : 'Y',
    remark: String(acl.remark || '').trim(),
    updatedAt: new Date().toISOString(),
  };
  if (!next.tenantCode) throw new Error('tenantCode가 필요합니다.');
  if (!next.formId) throw new Error('formId가 필요합니다.');
  const idx = pack.acls.findIndex((a) => a.aclId === id);
  if (idx >= 0) pack.acls[idx] = { ...pack.acls[idx], ...next };
  else pack.acls.push({ ...next, createdAt: new Date().toISOString() });
  pack.updatedAt = new Date().toISOString();
  writeJson(FORM_ACL_KEY, pack, true);
  return next;
}

export function deleteFormAcl(aclId, { hard = false } = {}) {
  const id = String(aclId || '').trim();
  if (!id) return false;
  const pack = readJson(FORM_ACL_KEY, { acls: [] });
  const idx = pack.acls.findIndex((a) => a.aclId === id);
  if (idx < 0) return false;
  if (hard) pack.acls.splice(idx, 1);
  else pack.acls[idx] = { ...pack.acls[idx], useYn: 'N', updatedAt: new Date().toISOString() };
  pack.updatedAt = new Date().toISOString();
  writeJson(FORM_ACL_KEY, pack, true);
  return true;
}

function normalizeCodeList(raw) {
  if (Array.isArray(raw)) {
    return [...new Set(raw.map((x) => String(x || '').trim()).filter(Boolean))];
  }
  return String(raw || '')
    .split(/[,;\s]+/)
    .map((x) => x.trim())
    .filter(Boolean);
}

/**
 * ACL 허용 여부. ACL 미등록(해당 기관·양식)이면 true.
 * @param {object|null} form
 * @param {{ tenantCode?: string, deptCode?: string, userId?: string }} ctx
 */
export function canUseForm(form, ctx = {}) {
  if (!form?.formId) return false;
  const tenantCode = String(ctx.tenantCode || '').trim();
  const acls = listFormAcls({ tenantCode, formId: form.formId });
  if (!acls.length) return true;
  const dept = String(ctx.deptCode || '').trim();
  const userId = String(ctx.userId || '').trim();
  return acls.some((a) => {
    const depts = a.allowDeptCodes || [];
    const users = a.allowUserIds || [];
    if (!depts.length && !users.length) return true;
    if (depts.length && dept && depts.includes(dept)) return true;
    if (users.length && userId && users.includes(userId)) return true;
    return false;
  });
}

function specificityScore(rule, ctx) {
  let score = 0;
  const checks = [
    ['deptCode', ctx.deptCode],
    ['projectCode', ctx.projectCode],
    ['fundCode', ctx.fundCode],
    ['fiscalYear', ctx.fiscalYear],
  ];
  for (const [key, ctxVal] of checks) {
    const rv = String(rule[key] || '').trim();
    if (!rv) continue;
    if (!ctxVal || String(ctxVal).trim() !== rv) return -1; // mismatch
    score += 1;
  }
  return score;
}

function pickUsableForm(form, ctx) {
  if (!form || form.useYn === 'N') return null;
  if (!canUseForm(form, ctx)) return null;
  return form;
}

/**
 * docType + 작성 컨텍스트 → 양식 메타 (매칭 + ACL)
 * @param {string} docType
 * @param {{ tenantCode?: string, deptCode?: string, projectCode?: string, fundCode?: string, fiscalYear?: string, userId?: string }} ctx
 */
export function resolveForm(docType, ctx = {}) {
  ensureFormRegistry();
  const tenantCode = String(ctx.tenantCode || '').trim();

  const rules = listMatchRules({ docType, tenantCode }).filter((r) => {
    if (r.tenantCode && r.tenantCode !== '*' && r.tenantCode !== tenantCode) return false;
    return r.docType === docType;
  });

  const scored = rules
    .map((r) => ({ rule: r, score: specificityScore(r, ctx) }))
    .filter((x) => x.score >= 0)
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return (b.rule.priority || 0) - (a.rule.priority || 0);
    });

  for (const { rule } of scored) {
    const form = pickUsableForm(getForm(rule.formId), ctx);
    if (form) return form;
  }

  if (tenantCode) {
    const slot = getTenantFormBindings(tenantCode, docType);
    const active = (slot.bindings || []).find((b) => b.activeYn === 'Y');
    if (active) {
      const form = pickUsableForm(getForm(active.formId), ctx);
      if (form) return form;
    }
  }

  const catalog = listForms({ docType, useYn: 'Y' });
  for (const f of catalog) {
    const form = pickUsableForm(f, ctx);
    if (form) return form;
  }
  return null;
}

/** 사업계획 호환: 기관 활성 양식 (+선택 ctx) */
export function resolveActiveFormForTenant(tenantCode, docType = DOC_TYPE.BIZ_PLAN, ctx = {}) {
  return resolveForm(docType, { tenantCode, ...ctx });
}

export function formationLinkedFields(form) {
  return (form?.fields || []).filter((f) => f.syncToFormation);
}

export function dbLinkedFields(form) {
  return (form?.fields || []).filter((f) => f.syncToDb);
}

/**
 * HWP 파일 업로드 → 템플릿 IDB + 카탈로그 등록
 * @param {{
 *   docType: string,
 *   formName?: string,
 *   file: File,
 *   copyFieldsFromFormId?: string,
 *   tenantCode?: string,
 *   makeActive?: boolean,
 * }} opts
 */
export async function registerFormUpload(opts) {
  const {
    docType,
    formName,
    file,
    copyFieldsFromFormId,
    tenantCode,
    makeActive = false,
  } = opts || {};
  if (!docType) throw new Error('docType이 필요합니다.');
  if (!file) throw new Error('HWP 파일이 필요합니다.');
  const name = String(file.name || '').toLowerCase();
  if (!name.endsWith('.hwp') && !name.endsWith('.hwpx')) {
    throw new Error('HWP/HWPX 파일만 업로드할 수 있습니다.');
  }

  const { saveFormTemplateHwp } = await import('system/formTemplateFileStore');
  const { createFormId } = await import('budget/bizFormCatalogSeed');

  const formId = createFormId('form');
  const bytes = new Uint8Array(await file.arrayBuffer());
  await saveFormTemplateHwp(formId, bytes, file.name);

  const base = copyFieldsFromFormId ? getForm(copyFieldsFromFormId) : null;
  const fields = base?.fields?.length
    ? JSON.parse(JSON.stringify(base.fields))
    : [];

  const meta = {
    formId,
    docType,
    formName: String(formName || '').trim() || file.name.replace(/\.hwpx?$/i, ''),
    description: `업로드 ${new Date().toISOString().slice(0, 10)}`,
    templateFile: file.name,
    publicUrl: '',
    hasUploadedTemplate: true,
    fieldMapId: formId,
    mapVersion: 1,
    module: 'BD',
    useYn: 'Y',
    fields,
    createdAt: new Date().toISOString(),
    note: copyFieldsFromFormId
      ? `필드맵 복사: ${copyFieldsFromFormId}`
      : '필드맵 없음 — 복사 원본을 지정하거나 P3에서 편집',
  };
  upsertForm(meta);

  if (tenantCode && makeActive) {
    bindFormToTenant(tenantCode, docType, formId, { makeActive: true });
  }
  return meta;
}

/** 기존 formId에 HWP만 교체(재업로드) */
export async function replaceFormTemplate(formId, file) {
  const form = getForm(formId);
  if (!form) throw new Error('양식을 찾을 수 없습니다.');
  if (!file) throw new Error('HWP 파일이 필요합니다.');
  const { saveFormTemplateHwp } = await import('system/formTemplateFileStore');
  const bytes = new Uint8Array(await file.arrayBuffer());
  await saveFormTemplateHwp(form.formId, bytes, file.name);
  upsertForm({
    ...form,
    templateFile: file.name,
    hasUploadedTemplate: true,
    mapVersion: (Number(form.mapVersion) || 1) + 0, // 메타만 갱신
    note: `${form.note || ''} · 템플릿 재업로드 ${new Date().toISOString().slice(0, 10)}`.trim(),
  });
  return getForm(formId);
}

// 시드 상수 재노출 (호환)
export { FORM_ID_GOYANG_CULTURE_V1, FORM_ID_DJ_WATERWORKS_V1 };
